import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { ModalBody } from '@atlaskit/modal-dialog';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Stack, xcss } from '@atlaskit/primitives';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { ObservableSpinner } from '@confluence/infinite-scroll';
import {
	type TeamData,
	fetchTeamData,
} from '@confluence/permissions-principals/entry-points/fetchTeamData';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import { PRINCIPAL_TYPE } from '../constants';

import {
	ConfluenceGroupUsageType,
	SitePermissionType as ContentAccessSitePermissionType,
} from './__types__/AccessDetailsDialogContentAccessDetailsQuery';
import type {
	AccessDetailsDialogContentAccessDetailsQuery as AccessDetailsDialogContentAccessDetailsQuery$data,
	AccessDetailsDialogContentAccessDetailsQuery_contentPermissions_ContentPermissions_subjectsWithEffectiveContentPermissions_PaginatedSubjectUserOrGroupList_nodes_SubjectUserOrGroup as AccessDetailsContentQueryNodesSubject,
} from './__types__/AccessDetailsDialogContentAccessDetailsQuery';
import type {
	AccessDetailsDialogSpaceAccessDetailsQuery_spacePermissions_SpacePermissions_subjectsWithPermissions_PaginatedSpacePermissionSubjectList_nodes_SpacePermissionSubject as AccessDetailsSpaceQueryNodesSubject,
	AccessDetailsDialogSpaceAccessDetailsQuery as AccessDetailsDialogSpaceAccessDetailsQuery$data,
} from './__types__/AccessDetailsDialogSpaceAccessDetailsQuery';
import {
	ConfluencePrincipalType as SpaceAccessPrincipalType,
	SitePermissionType as SpaceAccessSitePermissionType,
} from './__types__/AccessDetailsDialogSpaceAccessDetailsQuery';
import { AccessDetailsPrincipal } from './AccessDetailsPrincipal';

const specificAccessContainer = xcss({
	paddingBottom: 'space.250',
});

const i18n = defineMessages({
	countOfSubjects: {
		id: 'space-access-details.access-details-dialog.count-of-subjects',
		defaultMessage:
			'{countOfSubjects, plural,=0 {No suggestions found.} one {# suggestion found.} other {# suggestions found.}}',
		description: 'Accessible announcement for screen readers when the user types in the search box',
	},
});

type AccessDetailsDialogBodyProps = {
	spaceInfo?: AccessDetailsDialogSpaceAccessDetailsQuery$data;
	contentInfo?: AccessDetailsDialogContentAccessDetailsQuery$data;
	isLoading: boolean;
	hasInheritedRestrictions: boolean;
	handleSpaceAccessDetailsFetchMore: (_?: string | null) => void;
	handleContentAccessDetailsFetchMore: (_?: string | null) => void;
};

type AccessSubject = {
	id?: string | null;
	type: PRINCIPAL_TYPE;
	isGuest: boolean;
	displayName?: string | null;
	secondLine?: string | null;
	avatarUrl?: string;
	verified?: boolean;
};

function parseSpaceInfoSubjects(
	spaceInfo?: AccessDetailsDialogSpaceAccessDetailsQuery$data,
	extraTeamData?: TeamData[],
): AccessSubject[] {
	return (spaceInfo?.spacePermissions?.subjectsWithPermissions?.nodes || []).map((item) => {
		if (item?.subjectKey?.principalType === SpaceAccessPrincipalType.USER) {
			return {
				id: item?.subjectKey?.id,
				type: PRINCIPAL_TYPE.USER,
				displayName: item?.subjectKey?.displayName,
				avatarUrl: item?.subjectKey?.user?.profilePicture?.path || '',
				secondLine: item?.subjectKey?.user?.email,
				isGuest: item?.subjectKey?.user?.permissionType === SpaceAccessSitePermissionType.EXTERNAL,
			};
		} else if (
			item?.subjectKey?.principalType === SpaceAccessPrincipalType.GROUP &&
			item.subjectKey?.group?.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION &&
			fg('confluence_frontend_perms_exp_teams_as_principals')
		) {
			return {
				id: item?.subjectKey?.id,
				type: PRINCIPAL_TYPE.TEAM,
				displayName: item?.subjectKey.displayName,
				isGuest:
					item?.subjectKey.group?.permissionType === ContentAccessSitePermissionType.EXTERNAL,
				avatarUrl: extraTeamData?.find((team) => team.id === item.subjectKey?.id)
					?.largeAvatarImageUrl,
				verified: extraTeamData?.find((team) => team.id === item.subjectKey?.id)?.isVerified,
			};
		}

		// else not a user, so it must be a group
		return {
			id: item?.subjectKey?.id,
			type: PRINCIPAL_TYPE.GROUP,
			displayName: item?.subjectKey?.displayName,
			isGuest: item?.subjectKey?.group?.permissionType === SpaceAccessSitePermissionType.EXTERNAL,
		};
	});
}

function parseContentInfoSubjects(
	contentInfo?: AccessDetailsDialogContentAccessDetailsQuery$data,
	extraTeamData?: TeamData[],
): AccessSubject[] {
	return (
		contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.nodes || []
	).map((item) => {
		if (item?.type === PRINCIPAL_TYPE.USER) {
			return {
				id: item?.id,
				type: PRINCIPAL_TYPE.USER,
				displayName: item?.displayName,
				isGuest: item?.user?.permissionType === ContentAccessSitePermissionType.EXTERNAL,
				avatarUrl: item?.user?.profilePicture?.path || '',
				secondLine: item?.user?.email,
			};
		} else if (
			item?.type === PRINCIPAL_TYPE.GROUP &&
			item.group?.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION
		) {
			return {
				id: item?.id,
				type: PRINCIPAL_TYPE.TEAM,
				displayName: item?.displayName,
				isGuest: item?.group?.permissionType === ContentAccessSitePermissionType.EXTERNAL,
				avatarUrl: extraTeamData?.find((team) => team.id === item.id)?.largeAvatarImageUrl,
				verified: extraTeamData?.find((team) => team.id === item.id)?.isVerified,
			};
		}

		// else not a user, so it must be a group
		return {
			id: item?.id,
			type: PRINCIPAL_TYPE.GROUP,
			displayName: item?.displayName,
			isGuest: item?.group?.permissionType === ContentAccessSitePermissionType.EXTERNAL,
		};
	});
}

export const AccessDetailsDialogBody: FC<AccessDetailsDialogBodyProps> = ({
	spaceInfo,
	contentInfo,
	hasInheritedRestrictions,
	handleContentAccessDetailsFetchMore,
	handleSpaceAccessDetailsFetchMore,
	isLoading,
}) => {
	const intl = useIntl();
	const [subjectListCount, setSubjectListCount] = useState<number | null>(null);
	const [extraTeamData, setExtraTeamData] = useState<TeamData[]>([]);
	const { cloudId } = useSessionData();
	/*
          Removing bold highlighting of search match sections due to:
          https://asecurityteam.atlassian.net/browse/VULN-1084090

          Ticket for bringing that feature back:
          https://product-fabric.atlassian.net/browse/CPE-1437
     */

	const subjectList = hasInheritedRestrictions
		? parseContentInfoSubjects(contentInfo, extraTeamData)
		: parseSpaceInfoSubjects(spaceInfo, extraTeamData);

	const hasNextPage = hasInheritedRestrictions
		? Boolean(
				contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.pageInfo
					?.hasNextPage,
			)
		: Boolean(spaceInfo?.spacePermissions?.subjectsWithPermissions?.pageInfo?.hasNextPage);

	const nextPageCursor = hasInheritedRestrictions
		? contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.pageInfo?.endCursor
		: spaceInfo?.spacePermissions?.subjectsWithPermissions?.pageInfo?.endCursor;

	const handleFetchMore = hasInheritedRestrictions
		? handleContentAccessDetailsFetchMore
		: handleSpaceAccessDetailsFetchMore;

	useEffect(() => {
		// Delay to ensure Assistive Technology registers the live region before update
		// Also delay so screen readers announce the count last
		const timer = setTimeout(() => {
			setSubjectListCount(subjectList.length);
		}, 5000);
		return () => clearTimeout(timer);
	}, [subjectList]);

	useEffect(() => {
		const teamsFromSpaceInfo = (
			spaceInfo?.spacePermissions?.subjectsWithPermissions?.nodes || []
		).filter(
			(item) =>
				item?.subjectKey?.group &&
				item.subjectKey.group.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION,
		) as AccessDetailsSpaceQueryNodesSubject[];
		const teamIdsFromSpaceInfo: string[] = (teamsFromSpaceInfo || []).map(
			(item) => (item?.subjectKey?.group && item?.subjectKey?.group?.id) || '',
		);

		const fetchTeamDataFromAPI = async () => {
			if (
				teamIdsFromSpaceInfo &&
				teamIdsFromSpaceInfo.length > 0 &&
				fg('confluence_frontend_perms_exp_teams_as_principals')
			) {
				const resolveTeamData = async () => {
					const teamData = await fetchTeamData(cloudId, teamIdsFromSpaceInfo);
					return { teamData };
				};
				const { teamData } = await resolveTeamData();

				if (teamData) {
					setExtraTeamData(teamData);
				}
			}
		};
		void fetchTeamDataFromAPI();
	}, [spaceInfo, cloudId, spaceInfo?.spacePermissions?.subjectsWithPermissions?.nodes]);

	useEffect(() => {
		const teamsFromContentInfo = (
			contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.nodes || []
		).filter(
			(item) =>
				item && item?.group && item.group.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION,
		) as AccessDetailsContentQueryNodesSubject[];
		const teamIdsFromContentInfo: string[] = teamsFromContentInfo.map(
			(item) => (item?.group && item.group.id) || '',
		);

		const fetchTeamDataFromAPI = async () => {
			if (
				teamIdsFromContentInfo &&
				teamIdsFromContentInfo.length > 0 &&
				fg('confluence_frontend_perms_exp_teams_as_principals')
			) {
				const resolveTeamData = async () => {
					const teamData = await fetchTeamData(cloudId, teamIdsFromContentInfo);
					return { teamData };
				};
				const { teamData } = await resolveTeamData();

				if (teamData) {
					setExtraTeamData(teamData);
				}
			}
		};
		void fetchTeamDataFromAPI();
	}, [
		spaceInfo,
		cloudId,
		contentInfo?.contentPermissions?.subjectsWithEffectiveContentPermissions?.nodes,
	]);

	return (
		<ModalBody>
			<Stack
				space="space.150"
				alignInline="start"
				grow="fill"
				as="div"
				xcss={specificAccessContainer}
			>
				{subjectList.map((item) => (
					<AccessDetailsPrincipal {...item} key={item.id} />
				))}
				<VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
					{intl.formatMessage(i18n.countOfSubjects, { countOfSubjects: subjectListCount })}
				</VisuallyHidden>
			</Stack>
			{!isLoading && hasNextPage && (
				<ObservableSpinner
					onScrollTo={() => {
						handleFetchMore(nextPageCursor);
					}}
					cursor={nextPageCursor}
					testId="access-details-spinner"
					observerOptions={{
						root: null,
						rootMargin: '0px',
						threshold: 0.05,
					}}
				/>
			)}
		</ModalBody>
	);
};
